<template>
  <div class="qr-code-generator">
    <div class="qr-container">
      <div class="qr-code" ref="qrCodeRef"></div>
      <div class="qr-info">
        <h3>{{ $t('astrologyQr.scanTitle') }}</h3>
        <p>{{ $t('astrologyQr.scanDescription') }}</p>
      </div>
    </div>
    
    <div class="test-button-container">
      <button 
        class="test-button"
        @click="handleTestClick"
      >
        {{ $t('astrologyQr.testButton') }}
      </button>
      <p class="button-description">{{ $t('astrologyQr.buttonDescription') }}</p>
      <p class="ai-bot-notice" role="note">{{ $t('aiDisclosure.botNotice') }}</p>
    </div>

    <!-- Desktop/Tablet QR Popup -->
    <teleport to="body">
      <div v-if="showQrPopup" class="qr-modal-overlay" @click.self="closeQrPopup" role="dialog" aria-modal="true" aria-labelledby="qr-modal-title">
        <div class="qr-modal">
          <button class="qr-modal-close" @click="closeQrPopup" :aria-label="$t('astrologyQr.closePopupAria')">×</button>
          <h3 class="qr-modal-title" id="qr-modal-title">{{ $t('astrologyQr.modalTitle') }}</h3>
          <p class="qr-modal-subtitle">{{ $t('astrologyQr.modalSubtitle') }}</p>
          <div class="qr-halo">
            <div class="qr-modal-code" ref="qrPopupRef"></div>
          </div>
          <div class="qr-modal-separator"><span>{{ $t('astrologyQr.or') }}</span></div>
          <div class="qr-modal-instructions">
            <div class="instruction-list">
              <div class="instruction-item">
                <div class="instruction-icon">📱</div>
                <div class="instruction-content">
                  <div class="instruction-title">{{ $t('astrologyQr.instagramTitle') }}</div>
                  <div class="instruction-text">{{ $t('astrologyQr.instagramText') }}</div>
                </div>
              </div>
              <div class="instruction-separator"><span>{{ $t('astrologyQr.or') }}</span></div>
              <div class="instruction-item">
                <div class="instruction-icon">💬</div>
                <div class="instruction-content">
                  <div class="instruction-title">{{ $t('astrologyQr.chatTitle') }}</div>
                  <div class="instruction-text">{{ $t('astrologyQr.chatText') }}</div>
                  <p class="ai-bot-notice-modal" role="note">{{ $t('aiDisclosure.botNotice') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import QRCode from 'qrcode'
import { hasAnalyticsConsent } from '../utils/analyticsConsent.js'

export default {
  name: 'AstrologyQRGenerator',
  data() {
    return {
      instagramUrl: 'https://ig.me/m/_innerwisdom_?ref=68f0fc130bb58a162209ee6e',
      showQrPopup: false,
      prevOverflow: ''
    }
  },
  mounted() {
    this.generateQRCode()
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeUnmount() {
    if (this.showQrPopup) {
      this.unlockScroll()
    }
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    async generateQRCode() {
      try {
        const canvas = await QRCode.toCanvas(this.instagramUrl, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        })
        
        // Clear previous content
        this.$refs.qrCodeRef.innerHTML = ''
        
        // Add the canvas to the DOM
        this.$refs.qrCodeRef.appendChild(canvas)
        
        // Add some styling to the canvas
        canvas.style.borderRadius = '10px'
        canvas.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)'
      } catch (error) {
        console.error('Error generating QR code:', error)
        this.$refs.qrCodeRef.innerHTML = `<p>${this.$t('astrologyQr.qrError')}</p>`
      }
    },
    
    handleTestClick() {
      this.firePulsePixelEvent({
        action: 'test',
        type: 'astro_rechner'
      })

      // On mobile: follow deep link via parent; on desktop/tablet: show QR popup
      if (this.isMobileViewport()) {
        this.$emit('instagram-button-click', 'horoskop')
      } else {
        this.lockScroll()
        this.showQrPopup = true
        this.$nextTick(() => {
          this.generatePopupQRCode()
        })
      }
    },
    firePulsePixelEvent(payload, attempt = 0) {
      if (!hasAnalyticsConsent()) {
        return
      }

      const spPin = window.SP_PIN
      if (spPin && typeof spPin.fireEvent === 'function') {
        spPin.fireEvent('077437c1-6270-4c88-9cb9-1096d8f75094', payload)
        return
      }

      if (attempt < 10) {
        setTimeout(() => {
          this.firePulsePixelEvent(payload, attempt + 1)
        }, 300)
      }
    },
    isMobileViewport() {
      return window.innerWidth < 768
    },
    async generatePopupQRCode() {
      try {
        const canvas = await QRCode.toCanvas(this.instagramUrl, {
          width: 260,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        })
        this.$refs.qrPopupRef.innerHTML = ''
        this.$refs.qrPopupRef.appendChild(canvas)
        canvas.style.borderRadius = '12px'
        canvas.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.25)'
      } catch (error) {
        console.error('Error generating Popup QR code:', error)
        this.$refs.qrPopupRef.innerHTML = `<p>${this.$t('astrologyQr.qrError')}</p>`
      }
    },
    closeQrPopup() {
      this.showQrPopup = false
      this.unlockScroll()
    },
    lockScroll() {
      this.prevOverflow = document.documentElement.style.overflow
      document.documentElement.style.overflow = 'hidden'
    },
    unlockScroll() {
      document.documentElement.style.overflow = this.prevOverflow || ''
    },
    handleKeydown(e) {
      if (e.key === 'Escape' && this.showQrPopup) {
        this.closeQrPopup()
      }
    },
  }
}
</script>

<style scoped>
.qr-code-generator {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  padding: 30px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-width: 400px;
  margin: 0 auto;
}

.qr-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.qr-code {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.qr-info {
  text-align: center;
}

.qr-info h3 {
  font-size: 1.3rem;
  margin-bottom: 10px;
  color: var(--text-primary);
  background: var(--gradient-green-blue);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.qr-info p {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0;
}

.test-button-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.test-button {
  display: inline-block;
  background: rgba(139, 92, 246, 0.15);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  color: white;
  padding: 15px 30px;
  border-radius: 25px;
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: 600;
  transition: all 0.25s ease;
  box-shadow: 0 8px 24px rgba(217, 70, 239, 0.28);
  border: 1px solid rgba(255,255,255,0.15);
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.test-button:hover {
  transform: translateY(-2px);
  background: rgba(139, 92, 246, 0.25);
  backdrop-filter: blur(25px) saturate(200%);
  -webkit-backdrop-filter: blur(25px) saturate(200%);
  border-color: rgba(255, 255, 255, 0.25);
  box-shadow: 0 12px 28px rgba(217, 70, 239, 0.35);
}

.test-button:active {
  transform: translateY(0);
}

.button-description {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin: 0;
  text-align: center;
}

/* Popup Styles */
.qr-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.qr-modal {
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  padding: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  position: relative;
}

.qr-modal-close {
  position: absolute;
  top: 10px;
  right: 12px;
  background: transparent;
  border: none;
  color: var(--text-primary);
  font-size: 24px;
  cursor: pointer;
}

.qr-modal-title {
  margin: 0 0 16px 0;
  font-size: 1.2rem;
}

.qr-modal-subtitle {
  margin: -8px 0 16px 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.qr-modal-code {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: transparent;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.qr-halo {
  position: relative;
  padding: 18px;
  border-radius: 16px;
  background:
    radial-gradient(ellipse at center, rgba(102, 126, 234, 0.12), transparent 60%),
    radial-gradient(ellipse at center, rgba(118, 75, 162, 0.10), transparent 70%);
}

.qr-modal-separator {
  margin: 16px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-secondary);
}

.qr-modal-separator::before,
.qr-modal-separator::after {
  content: '';
  height: 1px;
  background: var(--border-color);
  flex: 1 1 auto;
}

.qr-modal-separator span {
  font-weight: 800;
  letter-spacing: 2px;
}

.qr-modal-link {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.qr-modal-instructions {
  margin-top: 16px;
  color: var(--text-secondary);
}

.instruction-list {
  display: grid;
  gap: 12px;
}

.instruction-item {
  display: grid;
  grid-template-columns: 36px 1fr;
  align-items: start;
  gap: 10px;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--glass-bg);
}

.instruction-icon {
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  background: var(--gradient-primary);
  color: var(--text-primary);
}

.instruction-title {
  font-weight: 700;
  color: var(--text-primary);
}

.instruction-text {
  color: var(--text-secondary);
}

.instruction-separator {
  margin: 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-secondary);
}

.instruction-separator::before,
.instruction-separator::after {
  content: '';
  height: 1px;
  background: var(--border-color);
  flex: 1 1 auto;
}

.instruction-separator span {
  font-weight: 800;
  letter-spacing: 2px;
}

.kbd {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background: rgba(0,0,0,0.15);
  font-weight: 700;
}

.button-description {
  margin-top: 8px;
  font-size: 0.9rem;
  opacity: 0.85;
}

.ai-bot-notice,
.ai-bot-notice-modal {
  margin: 10px 0 0;
  padding: 8px 10px;
  font-size: 0.78rem;
  line-height: 1.4;
  color: #00ffb3;
  background: rgba(0, 255, 179, 0.08);
  border: 1px solid rgba(0, 255, 179, 0.25);
  border-radius: 8px;
}

.qr-link-text {
  background: rgba(0,0,0,0.1);
  color: var(--text-primary);
  padding: 6px 10px;
  border-radius: 8px;
}

.qr-copy {
  background: var(--gradient-primary);
  color: var(--text-primary);
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
}

@media (max-width: 768px) {
  .qr-code-generator {
    padding: 20px;
    gap: 25px;
  }
  
  .qr-info h3 {
    font-size: 1.2rem;
  }
  
  .qr-info p {
    font-size: 0.9rem;
  }
  
  .test-button {
    padding: 12px 25px;
    font-size: 1rem;
  }
}
</style>
